import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    buildWebVitalEvent,
    detectAiReferral,
    observeWebVitals
} from '../services/webVitals.mjs';
import { isAuthActionUrlActive } from '../utils/authActionQuery.mjs';
import { installGaCtaClickListener } from '../services/gaEvents';

const GOOGLE_ADS_ID = 'AW-17938538505';
const GOOGLE_ANALYTICS_ID = 'G-79JNFWTLCJ';
const GOOGLE_TAG_LOCATION_CHANGE_EVENT = 'google-tag-location-change';
const GOOGLE_TAG_SCRIPT_ID = 'stocktag-google-tag-script';
const AI_REFERRAL_SESSION_KEY = 'stocktag_ai_referral_visit_v1';

const ensureGoogleTagScript = () => {
    if (typeof document === 'undefined') return false;
    if (document.getElementById(GOOGLE_TAG_SCRIPT_ID)) return true;

    const script = document.createElement('script');
    script.id = GOOGLE_TAG_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`;
    document.head.appendChild(script);
    return true;
};

const ensureGoogleTagsConfigured = () => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return false;
    if (window.__stocktagGoogleTagsConfigured) return true;

    window.gtag('js', new Date());
    window.gtag('config', GOOGLE_ADS_ID, { send_page_view: false });
    window.gtag('config', GOOGLE_ANALYTICS_ID, { send_page_view: false });
    window.__stocktagGoogleTagsConfigured = true;
    return true;
};

const getConsentState = ({ analyticsConsent, advertisingConsent }) => ({
    analytics_storage: analyticsConsent ? 'granted' : 'denied',
    ad_storage: advertisingConsent ? 'granted' : 'denied',
    ad_user_data: advertisingConsent ? 'granted' : 'denied',
    ad_personalization: advertisingConsent ? 'granted' : 'denied'
});

const getCurrentPagePath = () => {
    if (typeof window === 'undefined') return '';
    return `${window.location.pathname || '/'}${window.location.search || ''}`;
};

const dispatchLocationChange = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event(GOOGLE_TAG_LOCATION_CHANGE_EVENT));
};

const trackGooglePageView = () => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

    const pagePath = getCurrentPagePath();
    if (!pagePath || window.__stocktagLastGooglePageView === pagePath) return;

    window.gtag('event', 'page_view', {
        send_to: GOOGLE_ANALYTICS_ID,
        page_title: document.title || '',
        page_location: window.location.href,
        page_path: pagePath
    });
    window.__stocktagLastGooglePageView = pagePath;
};

const getNavigationType = () => {
    if (typeof performance === 'undefined') return 'navigate';
    const navigationEntry = performance.getEntriesByType?.('navigation')?.[0];
    return String(navigationEntry?.type || 'navigate');
};

const getRelease = () => import.meta.env.VITE_APP_VERSION || 'unknown';

const trackAiReferralVisit = () => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    const referral = detectAiReferral({
        href: window.location.href,
        referrer: document.referrer
    });
    if (!referral) return;

    try {
        if (window.sessionStorage.getItem(AI_REFERRAL_SESSION_KEY)) return;
        window.sessionStorage.setItem(AI_REFERRAL_SESSION_KEY, '1');
    } catch {
        // The GA4 event can still be emitted when session storage is unavailable.
    }

    window.gtag('event', 'ai_referral_visit', {
        send_to: GOOGLE_ANALYTICS_ID,
        ai_source: referral.source,
        landing_route: referral.landingPath,
        traffic_medium: referral.medium
    });
};

const AnalyticsLoader = ({ preferences }) => {
    const analyticsConsent = Boolean(preferences?.analytics);
    const advertisingConsent = Boolean(preferences?.advertising);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

        window.gtag('consent', 'update', getConsentState({
            analyticsConsent,
            advertisingConsent
        }));

        if (!analyticsConsent && !advertisingConsent) return;
        // Firebase auth-action links carry a one-time oobCode in the URL:
        // never load or configure the Google tag while one is present.
        if (isAuthActionUrlActive()) return;
        ensureGoogleTagScript();
        ensureGoogleTagsConfigured();
    }, [advertisingConsent, analyticsConsent]);

    useEffect(() => {
        if (!analyticsConsent || isAuthActionUrlActive() || !ensureGoogleTagsConfigured() || typeof window === 'undefined') {
            return undefined;
        }

        ensureGoogleTagScript();

        const emitPageView = () => {
            trackGooglePageView();
        };

        const originalPushState = window.history.pushState;
        const originalReplaceState = window.history.replaceState;

        window.history.pushState = function patchedPushState(...args) {
            const result = originalPushState.apply(this, args);
            dispatchLocationChange();
            return result;
        };

        window.history.replaceState = function patchedReplaceState(...args) {
            const result = originalReplaceState.apply(this, args);
            dispatchLocationChange();
            return result;
        };

        emitPageView();
        trackAiReferralVisit();
        window.addEventListener('popstate', emitPageView);
        window.addEventListener('hashchange', emitPageView);
        window.addEventListener(GOOGLE_TAG_LOCATION_CHANGE_EVENT, emitPageView);

        return () => {
            window.history.pushState = originalPushState;
            window.history.replaceState = originalReplaceState;
            window.removeEventListener('popstate', emitPageView);
            window.removeEventListener('hashchange', emitPageView);
            window.removeEventListener(GOOGLE_TAG_LOCATION_CHANGE_EVENT, emitPageView);
        };
    }, [analyticsConsent]);

    useEffect(() => {
        if (!analyticsConsent || isAuthActionUrlActive() || !ensureGoogleTagsConfigured() || typeof window === 'undefined') {
            return undefined;
        }

        const initialPathname = window.location.pathname || '/';
        const locale = document.documentElement.lang || 'en';
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
        const navigationType = getNavigationType();
        const release = getRelease();

        return observeWebVitals(({ metric, value }) => {
            const event = buildWebVitalEvent({
                metric,
                value,
                pathname: initialPathname,
                locale,
                viewportWidth,
                navigationType,
                release
            });
            if (!event || typeof window.gtag !== 'function') return;

            window.gtag('event', 'web_vital', {
                send_to: GOOGLE_ANALYTICS_ID,
                metric_name: event.metric,
                metric_value: event.value,
                metric_rating: event.rating,
                route_template: event.routeTemplate,
                content_locale: event.locale,
                device_class: event.deviceClass,
                navigation_type: event.navigationType,
                release: event.release,
                non_interaction: true
            });
        });
    }, [analyticsConsent]);

    useEffect(() => {
        if (!analyticsConsent || isAuthActionUrlActive() || !ensureGoogleTagsConfigured() || typeof window === 'undefined') {
            return undefined;
        }

        // One delegated capture-phase listener classifies conversion links
        // (studio, login, pricing, tool pages, ...) into cta_click events.
        return installGaCtaClickListener();
    }, [analyticsConsent]);

    return null;
};

AnalyticsLoader.propTypes = {
    preferences: PropTypes.shape({
        essential: PropTypes.bool,
        analytics: PropTypes.bool,
        advertising: PropTypes.bool
    }).isRequired
};

export default AnalyticsLoader;
